import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const YELLOW = [234, 179, 8]; // #eab308
const BLACK = [10, 10, 10];
const GREY = [113, 113, 122];
const DARK = [39, 39, 42];
const LIGHT = [245, 245, 244];

function fmtDate(iso, opts = {}) {
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
  return d.toLocaleDateString("en-GB", {
    weekday: opts.long ? "long" : "short",
    day: "2-digit",
    month: "short",
  });
}

function pretty(iso) {
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function stamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
}

export function downloadWeeklyDigestPdf(digest) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const w = doc.internal.pageSize.getWidth();
  const marginX = 48;

  // --- Masthead
  doc.setFillColor(...BLACK);
  doc.rect(0, 0, w, 96, "F");
  doc.setFillColor(...YELLOW);
  doc.rect(0, 96, w, 4, "F");

  doc.setTextColor(...YELLOW);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("WEEKLY DIGEST", marginX, 38);

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.text("LOAD / YARD", marginX, 64);

  doc.setTextColor(200, 200, 200);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const periodText = `${pretty(digest.period.start)} — ${pretty(digest.period.end)} · ${digest.period.timezone}`;
  doc.text(periodText, marginX, 82);

  // --- Totals row
  let y = 130;
  doc.setTextColor(...BLACK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("THE WEEK IN NUMBERS", marginX, y);
  y += 14;

  const totals = [
    { label: "Lorries", value: digest.totals.lorries },
    { label: "Tons Moved", value: digest.totals.tons },
    { label: "Products", value: digest.totals.products },
    { label: "Drivers", value: digest.totals.drivers },
  ];
  const cardW = (w - marginX * 2 - 12 * 3) / 4;
  totals.forEach((t, i) => {
    const x = marginX + i * (cardW + 12);
    doc.setDrawColor(...DARK);
    doc.setFillColor(...LIGHT);
    doc.rect(x, y, cardW, 60, "F");
    doc.setFillColor(...YELLOW);
    doc.rect(x, y, 3, 60, "F");

    doc.setTextColor(...GREY);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(String(t.label).toUpperCase(), x + 12, y + 18);

    doc.setTextColor(...BLACK);
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text(String(t.value), x + 12, y + 46);
  });
  y += 90;

  // --- Daily totals table
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...BLACK);
  doc.text("DAILY BREAKDOWN", marginX, y);

  autoTable(doc, {
    startY: y + 6,
    margin: { left: marginX, right: marginX },
    head: [["Day", "Lorries", "Tons", "Products"]],
    body: digest.daily.map((d) => [
      fmtDate(d.date, { long: true }),
      d.lorries,
      d.tons,
      d.product_variety,
    ]),
    styles: { font: "helvetica", fontSize: 10, cellPadding: 6, textColor: BLACK },
    headStyles: { fillColor: BLACK, textColor: YELLOW, fontStyle: "bold", fontSize: 9 },
    alternateRowStyles: { fillColor: [250, 250, 249] },
    columnStyles: {
      1: { halign: "right" },
      2: { halign: "right" },
      3: { halign: "right" },
    },
  });

  // --- Top products
  y = doc.lastAutoTable.finalY + 24;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...BLACK);
  doc.text("TOP PRODUCTS", marginX, y);
  autoTable(doc, {
    startY: y + 6,
    margin: { left: marginX, right: marginX },
    head: [["Product", "Trips", "Tons"]],
    body: (digest.top_products.length
      ? digest.top_products
      : [{ product: "—", trips: 0, tons: 0 }]
    ).map((p) => [p.product, p.trips, p.tons]),
    styles: { font: "helvetica", fontSize: 10, cellPadding: 6, textColor: BLACK },
    headStyles: { fillColor: BLACK, textColor: YELLOW, fontStyle: "bold", fontSize: 9 },
    alternateRowStyles: { fillColor: [250, 250, 249] },
    columnStyles: {
      1: { halign: "right" },
      2: { halign: "right" },
    },
  });

  // --- Top drivers
  y = doc.lastAutoTable.finalY + 24;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...BLACK);
  doc.text("TOP DRIVERS", marginX, y);
  autoTable(doc, {
    startY: y + 6,
    margin: { left: marginX, right: marginX },
    head: [["Driver", "Trips", "Tons"]],
    body: (digest.top_drivers.length
      ? digest.top_drivers
      : [{ driver: "—", trips: 0, tons: 0 }]
    ).map((d) => [d.driver, d.trips, d.tons]),
    styles: { font: "helvetica", fontSize: 10, cellPadding: 6, textColor: BLACK },
    headStyles: { fillColor: BLACK, textColor: YELLOW, fontStyle: "bold", fontSize: 9 },
    alternateRowStyles: { fillColor: [250, 250, 249] },
    columnStyles: {
      1: { halign: "right" },
      2: { halign: "right" },
    },
  });

  // --- Footer on every page
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(...DARK);
    doc.setLineWidth(0.5);
    doc.line(marginX, 812, w - marginX, 812);
    doc.setTextColor(...GREY);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("LOAD/YARD · Fleet Load Tracker", marginX, 826);
    doc.text(`Page ${i} of ${pageCount}`, w - marginX, 826, { align: "right" });
  }

  const filename = `loadyard-digest-${digest.period.start}_to_${digest.period.end}-${stamp()}.pdf`;
  doc.save(filename);
  return filename;
}
